import { useApi } from '@contexts/ApiContext';
import { Box, Card, CardContent, CardHeader, Checkbox, Divider, FormControlLabel, FormGroup, FormLabel, Grid, InputAdornment, MenuItem, Select, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSettings } from '@contexts/SettingsContext';
import Button, { ButtonType } from '@components/topstep/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import HelpMessage from '@components/helpMessage';
import { useTradingAccount } from '@contexts/TradingAccountContext';
import { IUserSettingsModel, JsonDocument, PersonalDailyAction, PersonalDailyLimitsRequest, PersonalDailyLossLimitTrailingType, RootElement, SetRiskSymbolBlocksRequest, TradeLimitsModel, TradingAccountType } from '@api/userApi';
import { logException } from '@/helpers/exceptionHelper';
import { toast } from 'react-toastify';
import { StyledButton } from '@/components/styledComponents';
import { useDeviceContext } from '@/contexts/DeviceContext';
import CountdownTimer from './countdown';
import { useModal } from '@/contexts/ModalContext';
import dayjs from 'dayjs';
import { useRiskSettingsLockout } from '@/contexts/RiskSettingsLockoutContext';
import TradeClock from '@/components/settings/tradeClock';
import PersonalLockout from '@/components/settings/personalLockout';
import { handleAxiosErrorWithCallback } from '@/helpers/axiosHelper';
import { AxiosError } from 'axios';
import { formatPrice } from '@/helpers/formatter';
import SymbolBlocksModal from './symbolBlocksModal';
import SymbolBlocks from './symbolBlocks';

interface RiskSettingsProps {
  onSaved?: () => void;
  onCancelled?: () => void;
  showButtons?: boolean;
}

const RiskSettings: React.FC<RiskSettingsProps> = ({ onSaved, onCancelled, showButtons }) => {
  const settings = useSettings();
  const { tradingAccountApi, tradeLimitApi, symbolBlockApi, retryApiCall } = useApi();
  const { activeTradingAccount } = useTradingAccount();
  const { isMobile } = useDeviceContext();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [risk, setRisk] = useState<number | null>(0);
  const [toMake, setToMake] = useState<number | null>(0);
  const [autoApply, setAutoApply] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [personalDailyLossLimit, setPersonalDailyLossLimit] = useState<number | null>(0);
  const [personalDailyLossLimitAction, setPersonalDailyLossLimitAction] = useState<PersonalDailyAction>(PersonalDailyAction.None);
  const [personalDailyLossLimitTrailing, setPersonalDailyLossLimitTrailing] = useState(activeTradingAccount.personalDailyLossLimitTrailing);
  const [personalDailyProfitTarget, setPersonalDailyProfitTarget] = useState<number | null>(0);
  const [personalDailyProfitTargetAction, setPersonalDailyProfitTargetAction] = useState<PersonalDailyAction>(PersonalDailyAction.None);
  const [maxTradesPerDay, setMaxTradesPerDay] = useState<number | null>(0);
  const [maxTradesPerWeek, setMaxTradesPerWeek] = useState<number | null>(0);
  const [currentDayTrades, setCurrentDayTrades] = useState<number>(0);
  const [currentWeekTrades, setCurrentWeekTrades] = useState<number>(0);
  const [lockoutExpiration, setLockoutExpiration] = useState<dayjs.Dayjs | null>(null);
  const { openModal, lockedOutUntil, isLockedOut, doSettingsLockout } = useRiskSettingsLockout();
  const isFollower = activeTradingAccount && activeTradingAccount.isFollower;
  const [pdllTrailingType, setPdllTrailingType] = useState<PersonalDailyLossLimitTrailingType>(activeTradingAccount.pdllTrailingType);
  // Symbol blocks state
  const [blockedSymbols, setBlockedSymbols] = useState<string[]>([]);
  const [originalBlockedSymbols, setOriginalBlockedSymbols] = useState<string[]>([]);

  const ignoreMaxTradesChanges = React.useRef(false);

  const { hideModal, showModal } = useModal();

  const BORDER_RADIUS = '8px'; // Consistent border radius for all cards

  // Load initial blocked symbols
  useEffect(() => {
    if (activeTradingAccount?.accountId) {
      symbolBlockApi.getAll(activeTradingAccount.accountId).then((blocks) => {
        const symbolIds = blocks.map((block) => block.symbolId);
        setBlockedSymbols(symbolIds);
        setOriginalBlockedSymbols(symbolIds);
      });
    }
  }, [activeTradingAccount?.accountId, symbolBlockApi]);

  useEffect(() => {
    setCurrentDayTrades(activeTradingAccount.currentDayTrades);
    setCurrentWeekTrades(activeTradingAccount.currentWeekTrades);

    if (ignoreMaxTradesChanges.current) {
      return;
    }

    setMaxTradesPerDay(activeTradingAccount.dailyTradeLimit);
    setMaxTradesPerWeek(activeTradingAccount.weeklyTradeLimit);
  }, [activeTradingAccount.currentDayTrades, activeTradingAccount.currentWeekTrades, activeTradingAccount.dailyTradeLimit, activeTradingAccount.weeklyTradeLimit, activeTradingAccount]);

  const hasDll = useMemo(() => {
    return activeTradingAccount.type == TradingAccountType.Express || activeTradingAccount.type == TradingAccountType.Live;
  }, [activeTradingAccount.type]);

  const resetDailyLimits = useCallback(() => {
    setPersonalDailyLossLimit(activeTradingAccount.personalDailyLossLimit);
    setPersonalDailyLossLimitAction(activeTradingAccount.personalDailyLossLimitAction);
    setPersonalDailyProfitTarget(activeTradingAccount.personalDailyProfitTarget);
    setPersonalDailyProfitTargetAction(activeTradingAccount.personalDailyProfitTargetAction);
    setPersonalDailyLossLimitTrailing(activeTradingAccount.personalDailyLossLimitTrailing);
    setPdllTrailingType(activeTradingAccount.pdllTrailingType);
  }, [activeTradingAccount.personalDailyLossLimit, activeTradingAccount.personalDailyLossLimitAction, activeTradingAccount.personalDailyProfitTarget, activeTradingAccount.personalDailyProfitTargetAction, activeTradingAccount.personalDailyLossLimitTrailing, activeTradingAccount.pdllTrailingType
  ]);

  const pdllTrailingTypeToString = (type: PersonalDailyLossLimitTrailingType): string => {
    switch (type) {
      case PersonalDailyLossLimitTrailingType.UnrealizedGains:
        return "(unrealized)";
      case PersonalDailyLossLimitTrailingType.RealizedGains:
        return "(realized)";
      default:
        return "unknown";
    }
  };


  const openSymbolBlocksModal = useCallback(() => {
    showModal(<SymbolBlocksModal onClose={hideModal} />);
  }, [showModal, hideModal]);

  useEffect(() => {
    // If locked out, return risk settings to original values
    if (isLockedOut) {
      resetDailyLimits();

      // trade limits
      setMaxTradesPerDay(activeTradingAccount.dailyTradeLimit);
      setMaxTradesPerWeek(activeTradingAccount.weeklyTradeLimit);

      // reset blocked symbols
      setBlockedSymbols(originalBlockedSymbols);
    }
  }, [isLockedOut, resetDailyLimits, activeTradingAccount.dailyTradeLimit, activeTradingAccount.weeklyTradeLimit, originalBlockedSymbols]);

  useEffect(() => {
    setLockoutExpiration(activeTradingAccount?.lockoutExpiration);
  }, [activeTradingAccount?.lockoutExpiration]);

  useEffect(() => {
    resetDailyLimits();
  }, [resetDailyLimits]);

  useEffect(() => {
    setRisk(settings.risk);
    setToMake(settings.toMake);
    setAutoApply(settings.autoApply);
  }, [settings]);

  // Check if blocked symbols have changed
  const isSymbolBlocksChanged = useMemo(() => {
    if (blockedSymbols.length !== originalBlockedSymbols.length) return true;

    const originalSet = new Set(originalBlockedSymbols);
    const currentSet = new Set(blockedSymbols);

    for (const id of originalSet) {
      if (!currentSet.has(id)) return true;
    }

    for (const id of currentSet) {
      if (!originalSet.has(id)) return true;
    }

    return false;
  }, [blockedSymbols, originalBlockedSymbols]);

  const cancel = useCallback(() => {
    setRisk(settings.risk);
    setToMake(settings.toMake);
    setAutoApply(settings.autoApply);
    setMaxTradesPerDay(activeTradingAccount.dailyTradeLimit);
    setMaxTradesPerWeek(activeTradingAccount.weeklyTradeLimit);
    setPersonalDailyLossLimit(activeTradingAccount.personalDailyLossLimit);
    resetDailyLimits();
    setBlockedSymbols(originalBlockedSymbols);
    onCancelled && onCancelled();
  }, [settings, activeTradingAccount.personalDailyLossLimit, activeTradingAccount.dailyTradeLimit, activeTradingAccount.weeklyTradeLimit, resetDailyLimits, originalBlockedSymbols, onCancelled]);

  const isChangedAccountSettings = useMemo(() => {
    return (
      personalDailyLossLimit != activeTradingAccount.personalDailyLossLimit ||
      personalDailyLossLimitAction != activeTradingAccount.personalDailyLossLimitAction ||
      personalDailyLossLimitTrailing != activeTradingAccount.personalDailyLossLimitTrailing ||
      pdllTrailingType != activeTradingAccount.pdllTrailingType ||
      personalDailyProfitTarget != activeTradingAccount.personalDailyProfitTarget ||
      personalDailyProfitTargetAction != activeTradingAccount.personalDailyProfitTargetAction ||
      maxTradesPerDay != activeTradingAccount.dailyTradeLimit ||
      maxTradesPerWeek != activeTradingAccount.weeklyTradeLimit
    );
  }, [personalDailyLossLimit, personalDailyLossLimitAction, personalDailyProfitTarget, personalDailyProfitTargetAction, personalDailyLossLimitTrailing, pdllTrailingType, maxTradesPerDay, maxTradesPerWeek, activeTradingAccount]);

  const isChanged = useMemo(() => {
    return risk !== settings.risk || toMake !== settings.toMake || autoApply !== settings.autoApply || isChangedAccountSettings || isSymbolBlocksChanged || pdllTrailingType != activeTradingAccount.pdllTrailingType;
  }, [risk, toMake, autoApply, settings, isChangedAccountSettings, isSymbolBlocksChanged, pdllTrailingType, activeTradingAccount.pdllTrailingType]);

  const isSettingsChanged = useMemo(() => {
    return risk !== settings.risk || toMake !== settings.toMake || autoApply !== settings.autoApply;
  }, [risk, toMake, autoApply, settings]);

  const isPersonalLimitChanged = useMemo(() => {
    return (
      personalDailyLossLimit != activeTradingAccount.personalDailyLossLimit ||
      personalDailyLossLimitAction != activeTradingAccount.personalDailyLossLimitAction ||
      personalDailyLossLimitTrailing != activeTradingAccount.personalDailyLossLimitTrailing ||
      personalDailyProfitTarget != activeTradingAccount.personalDailyProfitTarget ||
      pdllTrailingType != activeTradingAccount.pdllTrailingType ||
      personalDailyProfitTargetAction != activeTradingAccount.personalDailyProfitTargetAction
    );
  }, [personalDailyLossLimit, pdllTrailingType, personalDailyLossLimitAction, personalDailyProfitTarget, personalDailyProfitTargetAction, personalDailyLossLimitTrailing, activeTradingAccount]);

  const isTradeLimitChanged = useMemo(() => {
    return maxTradesPerDay != activeTradingAccount.dailyTradeLimit || maxTradesPerWeek != activeTradingAccount.weeklyTradeLimit;
  }, [maxTradesPerDay, maxTradesPerWeek, activeTradingAccount]);

  const onSave = useCallback(() => {
    if (maxTradesPerDay != null && maxTradesPerDay != activeTradingAccount.dailyTradeLimit && maxTradesPerDay <= currentDayTrades) {
      toast('Max trades per day must be greater than the current day trades.', { type: 'error' });
      return;
    }

    if (maxTradesPerWeek != null && maxTradesPerWeek != activeTradingAccount.weeklyTradeLimit && maxTradesPerWeek <= currentWeekTrades) {
      toast('Max trades per week must be greater than the current week trades.', { type: 'error' });
      return;
    }

    if (personalDailyLossLimitAction != PersonalDailyAction.None && personalDailyLossLimit === null) {
      toast('Please enter a valid Personal Daily Loss Limit.', { type: 'error' });
      return;
    }

    if (personalDailyProfitTargetAction != PersonalDailyAction.None && personalDailyProfitTarget === null) {
      toast('Please enter a valid Personal Daily Profit Target.', { type: 'error' });
      return;
    }

    if (personalDailyLossLimitAction != PersonalDailyAction.None && personalDailyLossLimit > 100000 && personalDailyLossLimitTrailing) {
      toast('Please enter a valid Personal Daily Loss Limit between 0 - 100000.', { type: 'error' });
      return;
    }

    if (personalDailyLossLimitAction != PersonalDailyAction.None && personalDailyLossLimit > (activeTradingAccount.dailyLoss || 100000) && !personalDailyLossLimitTrailing) {
      toast(`Please enter a valid Personal Daily Loss Limit between 0 - ${activeTradingAccount.dailyLoss || 100000}.`, { type: 'error' });
      return;
    }

    if (personalDailyLossLimitAction != PersonalDailyAction.None && personalDailyLossLimitTrailing && pdllTrailingType == PersonalDailyLossLimitTrailingType.None) {
      toast('Please select a Trailing PDLL Type.', { type: 'error' });
      return;
    }

    setIsLoading(true);

    const promises = [];
    let hasError = false;

    ignoreMaxTradesChanges.current = true;

    if (isSettingsChanged) {
      const model: IUserSettingsModel = {
        risk,
        toMake,
        autoApply: autoApply,
        autoCenter: settings.autoCenter,
        soundNotifications: settings.soundNotifications,
        custom: new JsonDocument({ rootElement: new RootElement(settings.customSettings) })
      };
      promises.push(settings.save(model));
    }

    if (isPersonalLimitChanged) {
      const dailyLimits = new PersonalDailyLimitsRequest({
        tradingAccountId: activeTradingAccount.accountId,
        personalDailyLossLimit: personalDailyLossLimit,
        personalDailyLossLimitAction: personalDailyLossLimitAction,
        personalDailyProfitTarget: personalDailyProfitTarget,
        personalDailyProfitTargetAction: personalDailyProfitTargetAction,
        personalDailyLossLimitTrailing: personalDailyLossLimitTrailing,
        pdllTrailingType: pdllTrailingType
      });

      promises.push(
        retryApiCall(() => tradingAccountApi.setPersonalDailyLimits(dailyLimits))
          .then((response) => {
            if (response.success == false) {
              hasError = true;
              toast(response.errorMessage, { type: 'error', hideProgressBar: true });
            }
          })
          .catch((e) =>
            handleAxiosErrorWithCallback(e, (ex: AxiosError) => {
              hasError = true;
              if (ex.status == 0) {
                toast('Error saving personal daily limits. Your internet connection might not be stable.', { type: 'error', hideProgressBar: true });
              } else {
                toast('Error saving personal daily limits', { type: 'error', hideProgressBar: true });
              }
            })
          )
      );
    }

    if (isTradeLimitChanged) {
      const tradeLimits = new TradeLimitsModel({
        tradingAccountId: activeTradingAccount.accountId,
        dailyTradeLimit: maxTradesPerDay,
        weeklyTradeLimit: maxTradesPerWeek
      });

      promises.push(
        retryApiCall(() => tradeLimitApi.addTradeLimits(tradeLimits)).catch((e) =>
          handleAxiosErrorWithCallback(e, (ex: AxiosError) => {
            hasError = true;
            if (ex.status == 0) {
              toast('Error saving trade limits. Your internet connection might not be stable.', { type: 'error', hideProgressBar: true });
            } else {
              toast('Error saving trade limits', { type: 'error', hideProgressBar: true });
            }
          })
        )
      );
    }

    if (isSymbolBlocksChanged) {
      const symbolBlockRequest = new SetRiskSymbolBlocksRequest({
        accountId: activeTradingAccount.accountId,
        symbolBlocks: blockedSymbols
      });

      promises.push(
        retryApiCall(() => symbolBlockApi.set(symbolBlockRequest)).catch((e) => {
          hasError = true;
          handleAxiosErrorWithCallback(e, (ex: AxiosError) => {
            if (ex.status == 0) {
              toast('Error saving symbol blocks. Your internet connection might not be stable.', { type: 'error', hideProgressBar: true });
            } else {
              toast('Error saving symbol blocks', { type: 'error', hideProgressBar: true });
            }
          });
        })
      );
    }

    Promise.all(promises)
      .then(() => {
        ignoreMaxTradesChanges.current = false;
        if (hasError) {
          setIsLoading(false);
        } else {
          setMaxTradesPerDay(maxTradesPerDay);
          setMaxTradesPerWeek(maxTradesPerWeek);
          activeTradingAccount.dailyTradeLimit = maxTradesPerDay;
          activeTradingAccount.weeklyTradeLimit = maxTradesPerWeek;
          setPersonalDailyLossLimit(personalDailyLossLimit);
          setPersonalDailyLossLimitAction(personalDailyLossLimitAction);
          setPersonalDailyLossLimitTrailing(personalDailyLossLimitTrailing);
          setPersonalDailyProfitTarget(personalDailyProfitTarget);
          setPersonalDailyProfitTargetAction(personalDailyProfitTargetAction);
          setPdllTrailingType(pdllTrailingType);
          setOriginalBlockedSymbols(blockedSymbols);
          setIsLoading(false);
          onSaved && onSaved();
          toast('Settings saved', { type: 'success', hideProgressBar: true });
        }
      })
      .catch((e) => {
        ignoreMaxTradesChanges.current = false;
        setIsLoading(false);
        logException(e, 'Error saving settings');
        toast('Error saving settings', { type: 'error', hideProgressBar: true });
      });
  }, [
    risk,
    toMake,
    autoApply,
    settings,
    activeTradingAccount,
    personalDailyLossLimit,
    personalDailyLossLimitAction,
    personalDailyProfitTarget,
    personalDailyProfitTargetAction,
    personalDailyLossLimitTrailing,
    maxTradesPerDay,
    maxTradesPerWeek,
    currentDayTrades,
    currentWeekTrades,
    blockedSymbols,
    pdllTrailingType,
    retryApiCall,
    tradingAccountApi,
    tradeLimitApi,
    symbolBlockApi,
    onSaved,
    isSettingsChanged,
    isPersonalLimitChanged,
    isTradeLimitChanged,
    isSymbolBlocksChanged
  ]);

  const updateToMake = useCallback((input: string) => {
    const value = parseInt(input);
    if (value <= 0 || isNaN(value)) {
      setToMake(null);
      return;
    }
    setToMake(Math.max(0, value));
  }, []);

  const updateDailyLoss = useCallback(
    (input: string) => {
      let value = parseInt(input);
      if (value <= 0 || isNaN(value)) {
        setPersonalDailyLossLimit(null);
        return;
      }
      if (!personalDailyLossLimitTrailing) {
        let maxPDLL = activeTradingAccount.dailyLoss || Math.max(100000, Math.max(activeTradingAccount.startingBalance, activeTradingAccount.balance));
        if (activeTradingAccount.type == TradingAccountType.Sim || activeTradingAccount.type == TradingAccountType.Live || activeTradingAccount.type == TradingAccountType.None) value = Math.min(value, maxPDLL);
      }

      setPersonalDailyLossLimit(Math.max(0, value));
    },
    [activeTradingAccount.dailyLoss, activeTradingAccount.startingBalance, activeTradingAccount.balance, activeTradingAccount.type, personalDailyLossLimitTrailing]
  );

  const updatePersonalDailyProfitTarget = useCallback((input: string) => {
    const value = parseInt(input);
    if (value <= 0 || isNaN(value)) {
      setPersonalDailyProfitTarget(null);
      return;
    }
    setPersonalDailyProfitTarget(Math.max(0, value));
  }, []);

  const updateRisk = useCallback((input: string) => {
    const value = parseInt(input);
    if (value <= 0 || isNaN(value)) {
      setRisk(null);
      return;
    }
    setRisk(Math.max(0, value));
  }, []);

  const updateMaxTradesPerDay = useCallback((input: string) => {
    const value = parseInt(input);
    if (value <= 0 || isNaN(value)) {
      setMaxTradesPerDay(null);
      return;
    }
    setMaxTradesPerDay(Math.max(0, value));
  }, []);

  const updateMaxTradesPerWeek = useCallback((input: string) => {
    const value = parseInt(input);
    if (value <= 0 || isNaN(value)) {
      setMaxTradesPerWeek(null);
      return;
    }
    setMaxTradesPerWeek(Math.max(0, value));
  }, []);

  const renderTradesRemaining = (maxTrades, tradesCount, period) => {
    if (maxTrades === null || maxTrades <= 0) {
      return null;
    }

    return (
      <Card
        variant='outlined'
        sx={{
          mt: 2,
          mb: 1,
          borderRadius: BORDER_RADIUS,
          ...(isSmallScreen && { bgcolor: 'transparent', border: '1px solid rgba(255, 255, 255, 0.12)' })
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          {maxTrades > tradesCount ? (
            <>
              <Typography variant='body1' gutterBottom>
                Trades Remaining for {period}
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {`${maxTrades - tradesCount} Remaining`}
              </Typography>
            </>
          ) : (
            <Typography variant='body1'>You have reached your Max Trades {period === 'Today' ? 'Today' : 'this week.'}</Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Common card styling based on screen size
  const cardStyle = {
    height: '100%',
    borderRadius: BORDER_RADIUS,
    backgroundImage: 'none',
    backgroundColor: isMobile ? '#FFFFFF10' : '#1c1e23',
    overflowY: 'auto',
    maxHeight: '100%',
    ...(isSmallScreen && {
      boxShadow: 'none',
      overflowY: 'unset',
      maxHeight: 'unset'
    })
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: isMobile ? '#1c1e23' : 'transparent',
        overflowY: isSmallScreen ? 'auto' : 'hidden',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        height: isSmallScreen ? 'calc(100dvh - 120px)' : 'calc(100vh - 200px)',
        pb: isMobile && (isChanged || showButtons) ? '64px' : undefined,
      }}
    >
      <Grid container spacing={3} sx={{ flexGrow: 1, maxHeight: isSmallScreen ? undefined : 'calc(100% - 20px)' }}>
        {/* Section 1: Global Risk Settings */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            height: isSmallScreen ? 'auto' : '100%',
            maxHeight: '100%'
          }}
        >
          <Card
            elevation={isSmallScreen ? 0 : 2}
            sx={{
              ...cardStyle,
              height: isSmallScreen ? 'auto' : '100%',
              overflow: isSmallScreen ? 'visible' : 'auto'
            }}
          >
            <CardHeader title='Global Risk Settings' titleTypographyProps={{ variant: 'h6' }} sx={{ pb: 1 }} />
            <CardContent>
              <FormGroup>
                <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  Position Risk / Profit Brackets
                  <HelpMessage message="These values act like a traditional 'bracket order', and will automatically enter an order to close out the position if the Risk or Profit trigger is hit. Note: risk trigger is exited with a STOP MARKET order." />
                </FormLabel>
                <TextField label='Risk ($)' variant='outlined' value={risk || ''} type='number' InputProps={{ inputProps: { min: 0 } }} fullWidth sx={{ mb: 2 }} onChange={(e) => updateRisk(e.target.value)} />
                <TextField label='Profit ($)' variant='outlined' value={toMake || ''} type='number' InputProps={{ inputProps: { min: 0 } }} fullWidth sx={{ mb: 2 }} onChange={(e) => updateToMake(e.target.value)} />
                <FormControlLabel control={<Checkbox checked={autoApply} onChange={() => setAutoApply(!autoApply)} />} label='Automatically apply Risk / Profit bracket to new Positions' />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Section 2: Account Risk Settings - Now wider with internal columns */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            height: isSmallScreen ? 'auto' : '100%',
            maxHeight: '100%'
          }}
        >
          <Card
            elevation={isSmallScreen ? 0 : 2}
            sx={{
              ...cardStyle,
              height: isSmallScreen ? 'auto' : '100%',
              overflow: isSmallScreen ? 'visible' : 'auto',
              position: 'relative'
            }}
          >
            <CardHeader title='Account Risk Settings' titleTypographyProps={{ variant: 'h6' }} sx={{ pb: 1 }} />
            <CardContent>
              {(activeTradingAccount.type === TradingAccountType.Express || activeTradingAccount.type === TradingAccountType.Sim || activeTradingAccount.type === TradingAccountType.Live) && (
                <Grid container spacing={2}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* PDLL Section */}
                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        Personal Daily Loss Limit (PDLL)
                        <HelpMessage message='Set a personal daily loss limit on your account.' />
                      </FormLabel>
                      <TextField
                        data-intercom-target='pdll-input'
                        variant='outlined'
                        fullWidth
                        required={personalDailyLossLimitAction !== PersonalDailyAction.None}
                        value={personalDailyLossLimit || ''}
                        type='number'
                        size='small'
                        InputProps={{
                          startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                          inputProps: {
                            min: 0,
                            max: 100000
                          }
                        }}
                        onChange={(e) => updateDailyLoss(e.target.value)}
                        disabled={isLockedOut}
                        sx={{ mb: 2 }}
                      />
                      <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        PDLL Action
                        <HelpMessage message='Sets the action that occurs when your personal daily loss limit is triggered.' />
                      </FormLabel>
                      <Select disabled={isLockedOut} data-intercom-target='pdll-action' size='small' fullWidth sx={{ mb: 2 }} onChange={(v) => setPersonalDailyLossLimitAction(v.target.value as PersonalDailyAction)} value={personalDailyLossLimitAction}>
                        <MenuItem value={PersonalDailyAction.None}>Do Nothing</MenuItem>
                        <MenuItem value={PersonalDailyAction.Liquidate}>Liquidate</MenuItem>
                        <MenuItem value={PersonalDailyAction.LiquidateAndBlock}>Liquidate & Block</MenuItem>
                      </Select>
                      <FormControlLabel
                        control={<Checkbox checked={personalDailyLossLimitTrailing} onChange={(e) => setPersonalDailyLossLimitTrailing(e.target.checked)} disabled={isLockedOut} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Enable Trailing
                            <HelpMessage
                              message={`The Trailing Personal Daily Loss Limit trails the highest balance
                            ${activeTradingAccount.personalDailyLossLimitTrailing ? pdllTrailingTypeToString(activeTradingAccount.pdllTrailingType) : ""}
                             reached after enablement, allowing for a dynamic drawdown from the most recent peak.
                             ${activeTradingAccount.personalDailyLossLimitTrailing && activeTradingAccount.personalDailyLossLimitAction != PersonalDailyAction.None ?
                                  ` Trailing PDLL enabled at $${formatPrice(activeTradingAccount.pdllTrailingStartingBalance ?? activeTradingAccount.balance)}.` : ""
                                }`}
                            />
                          </Box>
                        }
                      />

                      {personalDailyLossLimitTrailing && (
                        <FormGroup>
                          <FormLabel>
                            Trailing PDLL Type
                            <HelpMessage message="Choose whether the Trailing PDLL should be based on unrealized gains or realized gains." />
                          </FormLabel>
                          <Select
                            value={pdllTrailingType}
                            onChange={(v) => setPdllTrailingType(v.target.value as any as PersonalDailyLossLimitTrailingType)}
                            sx={{
                              width: '18em',
                              border: pdllTrailingType === PersonalDailyLossLimitTrailingType.None && personalDailyLossLimitAction != PersonalDailyAction.None ? '2px solid red' : '1px solid #ccc',
                              borderRadius: '4px',
                            }}
                            size="small"
                          >
                            {activeTradingAccount.pdllTrailingType == PersonalDailyLossLimitTrailingType.None && (
                              <MenuItem value={PersonalDailyLossLimitTrailingType.None} key={PersonalDailyLossLimitTrailingType.None}>
                                -- Select --
                              </MenuItem>)
                            }
                            <MenuItem value={PersonalDailyLossLimitTrailingType.UnrealizedGains} key={PersonalDailyLossLimitTrailingType.UnrealizedGains}>
                              Unrealized Gains
                            </MenuItem>
                            <MenuItem value={PersonalDailyLossLimitTrailingType.RealizedGains} key={PersonalDailyLossLimitTrailingType.RealizedGains}>
                              Realized Gains
                            </MenuItem>
                          </Select>
                        </FormGroup>
                      )}
                    </Box>

                    {activeTradingAccount.type !== TradingAccountType.Live && (
                      <>
                        <Divider sx={{ my: 2 }} />

                        {/* Trade Limits Section */}
                        <Box sx={{ mb: 3 }}>
                          <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            Trade Limits
                            <HelpMessage
                              message={
                                <>
                                  Trade limits include completed round-turn trades. If the trade limit is reached while there are existing working orders, you can manage them beyond the limit, but only liquidating market orders will be accepted once the limit
                                  is reached.
                                  <br />
                                  <br />
                                  <strong>WARNING:</strong> Trade Limits on Trade Copier Follower accounts are ignored.
                                </>
                              }
                            />
                          </FormLabel>
                          <TextField
                            label='Maximum Trades per Day'
                            variant='outlined'
                            fullWidth
                            size='small'
                            value={maxTradesPerDay || ''}
                            type='number'
                            onChange={(e) => updateMaxTradesPerDay(e.target.value)}
                            disabled={isLockedOut || activeTradingAccount.currentDayTrades >= (activeTradingAccount.dailyTradeLimit ?? 99999) || activeTradingAccount.currentWeekTrades >= (activeTradingAccount.weeklyTradeLimit ?? 99999)}
                            InputProps={{
                              inputProps: {
                                min: activeTradingAccount.currentDayTrades ?? 0
                              }
                            }}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            label='Maximum Trades per Week'
                            variant='outlined'
                            fullWidth
                            size='small'
                            value={maxTradesPerWeek || ''}
                            type='number'
                            onChange={(e) => updateMaxTradesPerWeek(e.target.value)}
                            disabled={isLockedOut || activeTradingAccount.currentDayTrades >= (activeTradingAccount.dailyTradeLimit ?? 99999) || activeTradingAccount.currentWeekTrades >= (activeTradingAccount.weeklyTradeLimit ?? 99999)}
                            InputProps={{
                              inputProps: {
                                min: activeTradingAccount.currentWeekTrades ?? 0
                              }
                            }}
                            sx={{ mb: 2 }}
                          />
                          {renderTradesRemaining(maxTradesPerDay, currentDayTrades, 'Today')}
                          {renderTradesRemaining(maxTradesPerWeek, currentWeekTrades, 'This Week')}
                        </Box>
                      </>
                    )}
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* PDPT Section */}
                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        Personal Daily Profit Target (PDPT)
                        <HelpMessage message='Set a personal daily profit target for your account.' />
                      </FormLabel>
                      <TextField
                        disabled={isLockedOut}
                        variant='outlined'
                        fullWidth
                        size='small'
                        required={personalDailyProfitTargetAction !== PersonalDailyAction.None}
                        value={personalDailyProfitTarget || ''}
                        type='number'
                        InputProps={{
                          startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                          inputProps: {
                            min: 0,
                            max: 100000
                          }
                        }}
                        onChange={(e) => updatePersonalDailyProfitTarget(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        PDPT Action
                        <HelpMessage message='Sets the action that occurs when your personal daily profit target is triggered.' />
                      </FormLabel>
                      <Select disabled={isLockedOut} fullWidth size='small' onChange={(v) => setPersonalDailyProfitTargetAction(v.target.value as PersonalDailyAction)} value={personalDailyProfitTargetAction}>
                        <MenuItem value={PersonalDailyAction.None}>Do Nothing</MenuItem>
                        <MenuItem value={PersonalDailyAction.Liquidate}>Liquidate</MenuItem>
                        <MenuItem value={PersonalDailyAction.LiquidateAndBlock}>Liquidate & Block</MenuItem>
                      </Select>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Symbol Blocks */}
                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        Symbol Blocks
                        <HelpMessage message='Block specific symbols from being traded on this account.' />
                      </FormLabel>
                      <SymbolBlocks initialBlocks={blockedSymbols} onChange={setBlockedSymbols} />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Lockout Button */}
                    {!isLockedOut && (
                      <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <FormLabel sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          Lockout Risk Settings
                          <HelpMessage message='Prevents changes to the risk settings for the day.' />
                        </FormLabel>
                        <Button buttonType={ButtonType.red} disabled={isLoading || isChangedAccountSettings} onClick={openModal} style={{ width: '100%' }}>
                          LOCK OUT RISK SETTINGS FOR DAY
                        </Button>
                        {isChangedAccountSettings && (
                          <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1 }}>
                            You have unsaved changes.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* Lockout Overlay - remains outside the columns but inside the card */}
              {isLockedOut && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 999,
                    border: '2px solid rgba(255, 255, 0, 0.5)',
                    backdropFilter: 'blur(0.5px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: BORDER_RADIUS
                  }}
                >
                  <Typography variant='h5' sx={{ color: '#fff', textAlign: 'center', mb: 2 }}>
                    Risk Settings Locked Out
                    <HelpMessage
                      style={{ zIndex: 9999 }}
                      message='Your risk settings are currently locked for the remainder of the trading day. You will be able to make adjustments again when the next trading day begins. This helps you stay committed to your original risk plan and maintain discipline.'
                    />
                  </Typography>
                  <CountdownTimer
                    targetDate={lockedOutUntil}
                    style={{
                      color: 'yellow',
                      textAlign: 'center',
                      fontSize: '1.5em',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Section 3: Personal Lockouts */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            height: isSmallScreen ? 'auto' : '100%'
          }}
        >
          <Card
            elevation={isSmallScreen ? 0 : 2}
            sx={{
              ...cardStyle,
              height: isSmallScreen ? 'auto' : '100%',
              overflow: isSmallScreen ? 'visible' : 'auto'
            }}
          >
            <CardHeader title='Personal Lockouts' titleTypographyProps={{ variant: 'h6' }} sx={{ pb: 1 }} />
            <CardContent>
              <Box sx={{ mb: 4, width: '100%' }}>
                <PersonalLockout fullWidth />
              </Box>
              <Box>
                <TradeClock fullWidth />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      {(isChanged || showButtons) && (
        isMobile ? (
          <Box
            sx={{
              position: 'fixed',
              bottom: '64px',
              width: '100vw',
              zIndex: 2001,
              bgcolor: 'rgba(28,30,35)',
              display: 'flex',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <StyledButton color='error' disabled={isLoading} onClick={cancel} sx={{ mx: 1 }}>
              Cancel
            </StyledButton>
            <StyledButton color='success' disabled={isLoading} onClick={onSave} sx={{ mx: 1 }}>
              Save {isLoading && <FontAwesomeIcon spin spinPulse icon={faSpinner} />}
            </StyledButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <StyledButton color='error' disabled={isLoading} onClick={cancel} sx={{ mx: 1 }}>
              Cancel
            </StyledButton>
            <StyledButton color='success' disabled={isLoading} onClick={onSave} sx={{ mx: 1 }}>
              Save {isLoading && <FontAwesomeIcon spin spinPulse icon={faSpinner} />}
            </StyledButton>
          </Box>
        )
      )}
    </Box>
  );
};

export default RiskSettings;
